import { useRef, useState } from "react";
import { useDraftPrompt } from "../context/DraftPromptContext.jsx";
import { useMyMediaUpload } from "../context/MyMediaUploadContext.jsx";
import API from "../config/api.js";

// ─────────────────────────────────────────────────────────────────────────────
// Single item preview
// ─────────────────────────────────────────────────────────────────────────────
function ItemPreview({ item }) {
  const [playing, setPlaying] = useState(false);
  const isMedia = item.type === "image" || item.type === "story";
  const isVideo = item.type === "reel" || item.type === "video";
  const src = item.url?.startsWith("http") ? item.url : `${API.baseUrl}/${item.url}`;
  const thumb = isMedia
    ? src
    : `${API.baseUrl}/upload/${item.type}/${item.id}/image/1080x1920.jpg`;

  return (
    <div className="position-relative rounded-3 w-100 text-center" style={{ backgroundColor: "#f2f2f2" }}>
      {isMedia && (
        <img
          src="/assets/img/864x1080-transparent.png"
          className="rounded-3 img-fluid"
          style={{ background: `url(${src}) center / cover`, }}
          alt={item.id}
        />
      )}
      {isVideo && (
        <>
          <img
            src="/assets/img/1080x1920-transparent.png"
            className="rounded-3 w-100"
            style={{ background: `url(${thumb}) center / cover` }}
            alt={item.id}
          />
          {playing ? (
            <video
              className="w-100 h-100 rounded-3 position-absolute top-0 start-0 object-fit-contain"
              autoPlay
              onEnded={() => setPlaying(false)}
            >
              <source src={src} type="video/mp4" />
            </video>
          ) : null}
          <div
            className="position-absolute top-50 start-50 translate-middle w-100 text-center"
            style={{ cursor: "pointer" }}
            onClick={() => setPlaying((v) => !v)}
          >
            <img
              src={playing ? "/assets/img/icon/pause.png" : "/assets/img/icon/play.png"}
              alt="play"
            />
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Reorder thumbnail
// ─────────────────────────────────────────────────────────────────────────────
function ReorderItem({ item, index, total, onDragStart, onDragEnd, onDragOver, onDrop, onRemove }) {
  const isVideo = item.type === "reel" || item.type === "video";
  const thumb = isVideo
    ? `${API.baseUrl}/upload/${item.type}/${item.id}/image/50x50.jpg`
    : (item.url?.startsWith("http") ? item.url : `${API.baseUrl}/${item.url}`).replace("/original.", "/50x50.");

  return (
    <div
      className="col item animate__animated"
      draggable
      onDragStart={(e) => onDragStart(e, index)}
      onDragEnd={onDragEnd}
      onDragOver={(e) => { e.preventDefault(); }}
      onDrop={(e) => { e.preventDefault(); onDrop(index); }}
      data-index={index}
    >
      <div className="position-relative p-1">
        <img src={thumb} className="rounded-2 w-100 h-100" alt={item.id}
          onError={(e) => (e.target.src = "/assets/img/50x50.png")} />
        {isVideo && (
          <span className="position-absolute top-50 start-50 translate-middle">
            <i className="fi fi-rr-play text-white" />
          </span>
        )}
        <span className="position-absolute m-1 top-0 start-0 bg-primary text-white py-1 px-2 rounded-2">
          {index + 1}
        </span>
        <button
          type="button"
          className="btn btn-sm position-absolute top-0 end-0 text-white p-0"
          onClick={() => onRemove(item.type, item.id)}
        >
          <i className="fi fi-rr-cross-small" />
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
export default function DraftPromptItems() {
  const { state, patch, itemRemove, itemsReorder, closeDraftPrompt } = useDraftPrompt();
  const { openUpload } = useMyMediaUpload();
  const [activeIndex, setActiveIndex] = useState(0);
  const [showReorder, setShowReorder] = useState(false);
  const dragIndexRef = useRef(null);

  const items = state.items || [];
  const count = items.length;
  const hasItems = count > 0;

  const activeItem = items[Math.min(activeIndex, count - 1)] || null;

  // ── Drag-and-drop reorder ─────────────────────────────────────────────────
  const handleDragStart = (e, index) => { dragIndexRef.current = index; };
  const handleDragEnd = () => { dragIndexRef.current = null; };
  const handleDrop = (dropIndex) => {
    const from = dragIndexRef.current;
    if (from === null || from === dropIndex) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(dropIndex, 0, moved);
    itemsReorder(next);
    dragIndexRef.current = null;
  };

  const handlePrev = () => setActiveIndex((i) => Math.max(0, i - 1));
  const handleNext = () => setActiveIndex((i) => Math.min(count - 1, i + 1));

  // ── Upload cover for reel ─────────────────────────────────────────────────
  const coverInputRef = useRef(null);
  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await fetch(`${API.drafts}?addImage=true`, {
        method: "POST", credentials: "include", body: fd,
      });
      const data = await res.json();
      if (data.status === "success") {
        patch({ idimagecover: data.idimage });
      }
    } catch (err) { console.error("Cover upload:", err); }
    e.target.value = "";
  };

  const isReel = state.type === "reel";

  // ── Device upload: close DraftPrompt first, reopen after upload ───────────
  const handleDeviceUpload = () => {
    closeDraftPrompt();
    openUpload({
      type: "combined",
      prompt: "1",
      onUploaded: (id, uploadType) => {
        const url = uploadType === "image"
          ? `upload/image/${id}/image/original.jpg`
          : `upload/reel/${id}/file/file.mp4`;
        const itemType = uploadType === "image" ? "image" : "reel";
        window._draft_prompt_item_add?.(itemType, id, url);
      },
      onCancelled: () => {
        window._draft_prompt_open?.();
      },
    });
  };

  // ── Navigate to a route after closing the draft prompt ────────────────────
  const handleNavigate = (path) => {
    closeDraftPrompt();
    setTimeout(() => { window.location = path; }, 300);
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className={`${isReel ? "col-lg-5" : "col-lg-6"} p-2 pe-lg-0 m-0 position-relative`} id="draft-prompt-preview">
      <button
        type="button"
        className="d-block d-sm-none btn btn-close bg-white rounded-circle p-3 position-absolute end-0"
        style={{ zIndex: 1000 }}
        onClick={closeDraftPrompt}
        aria-label="Close"
      ></button>

      {/* Hidden cover input */}
      <input ref={coverInputRef} type="file" className="d-none" accept="image/png,image/jpeg" onChange={handleCoverUpload} />

      {/* ── Empty state ── */}
      {!hasItems && (
        <div className="position-relative animate__animated animate__fadeIn">
          <div className="my-4 pt-3 pb-2">
            <div className="position-absolute h-100 text-center p-3 w-100">
              <div className="position-absolute top-50 start-50 translate-middle w-100 text-center p-3">
                <img src="/assets/img/icon/draft-prompt-upload.png" alt="" />
                <h5>Upload your next post</h5>
                <p className="text-muted">Impress your followers</p>
                <div className="text-center">

                  <button
                    type="button"
                    className="btn btn-outline-primary me-2"
                    onClick={() => handleNavigate("images")}
                  >
                    Photos
                  </button>

                  <button
                    type="button"
                    className="btn btn-outline-primary me-2"
                    onClick={() => handleNavigate("reels")}
                  >
                    Reels
                  </button>

                  <div className="dropdown d-inline-block">
                    <button className="btn btn-primary dropdown-toggle" type="button" data-bs-toggle="dropdown">
                      + Upload
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end shadow-sm">
                      <li>
                        <button className="dropdown-item" type="button" onClick={handleDeviceUpload}>
                          <i className="fi fi-rr-folder me-2" /> Choose From My Device
                        </button>
                      </li>
                      <li>
                        <button className="dropdown-item" type="button" onClick={() => window._draft_prompt_show_my_media?.()}>
                          <i className="fi fi-rr-picture me-2" /> Choose From My Media
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <img src="/assets/img/864x1080-transparent.png" width="864" height="1080" alt="" className="bg-light rounded w-100 h-100 invisible" />
        </div>
      )}

      {/* ── Carousel ── */}
      {hasItems && activeItem && (
        <div className="position-relative">
          <ItemPreview item={activeItem} />

          {/* Dot indicators — Bootstrap positions these at bottom center by default */}
          {count > 1 && (
            <div className="carousel-indicators rounded-3 m-2 px-2 shadow-sm">
              {items.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActiveIndex(i)}
                  className={i === activeIndex ? "active" : ""}
                  aria-current={i === activeIndex ? "true" : undefined}
                />
              ))}
            </div>
          )}

          {/* X / Y badge — bottom center overlaid on image */}
          {count > 1 && (
            <div className="carousel-caption m-2">
              <span
                className="text-dark badge px-2 rounded-3 small shadow-sm"
                style={{ background: "rgb(255 255 255 / 70%)", backdropFilter: "blur(3px)" }}
              >
                {activeIndex + 1} / {count}
              </span>
            </div>
          )}

          {/* Prev / Next */}
          {count > 1 && (
            <>
              <button className="carousel-control-prev" type="button" onClick={handlePrev} style={{ opacity: activeIndex === 0 ? 0.3 : 1 }}>
                <span className="carousel-control-prev-icon" aria-hidden="true" />
              </button>
              <button className="carousel-control-next" type="button" onClick={handleNext} style={{ opacity: activeIndex === count - 1 ? 0.3 : 1 }}>
                <span className="carousel-control-next-icon" aria-hidden="true" />
              </button>
            </>
          )}

          {/* Reel cover button */}
          {isReel && (
            <button
              type="button"
              className="btn btn-dark position-absolute end-0 top-0 border-0"
              onClick={() => coverInputRef.current?.click()}
              style={{ background: "rgba(0,0,0,0.4)", marginTop: 20, marginRight: 12 }}
            >
              + Upload Cover
            </button>
          )}

          {/* ── Reorder panel — overlays the image from bottom ── */}
          {showReorder && (
            <div
              className="position-absolute bottom-0 w-100"
              style={{ zIndex: 10000 }}
            >
              <div>
                <div className="d-flex align-items-center justify-content-between px-2 py-1">
                  <span className="d-inline-block mb-1 p-1 px-3 rounded-2 fw-bold"  style={{ background: "rgba(255,255,255,0.7)", backdropFilter: "blur(3px)" }}>
                    <i className="fi fi-rr-cursor-finger text-primary fs-6 me-1" /> Drag and drop to change order
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm rounded-circle"
                    onClick={() => setShowReorder(false)}
                    style={{ background: "rgba(255,255,255,0.7)", width: 36, height: 36 }}
                  >
                    <i className="fi fi-rr-check" />
                  </button>
                </div>
                <div className="p-1 mx-2 rounded-3 mb-2" style={{ background: "rgba(255,255,255,0.7)", backdropFilter: "blur(3px)" }}>
                  <div className="row g-0 row-cols-3 row-cols-sm-4 row-cols-lg-5 rounded items">
                    {items.map((item, index) => (
                      <ReorderItem
                        key={`${item.type}-${item.id}`}
                        item={item}
                        index={index}
                        total={count}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onDrop={handleDrop}
                        onRemove={itemRemove}
                      />
                    ))}
                    {count < 10 && (
                      <div className="col">
                        <div className="position-relative h-100 w-100 p-1">
                          <div className="ratio ratio-1x1">
                            <button
                              style={{ background: "#ece6f9" }}
                              type="button"
                              className="btn text-primary h-100 w-100"
                              onClick={() => window._draft_prompt_show_my_media?.()}
                            >
                              <i className="fi fi-rr-plus fs-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Actions bar ── */}
      {hasItems && (
        <div className="bg-secondary-light text-center rounded-3 p-2 mt-2" id="draft-prompt-image-actions">
          {/* Add more */}
          <div className="dropdown d-inline-block mx-1">
            <button type="button" className="btn btn-secondary text-muted rounded-circle px-2" data-bs-toggle="dropdown">
              <i className="fi fi-rr-plus text-primary fs-6" />
            </button>
            <ul className="dropdown-menu dropdown-menu-end shadow-sm">
              <li>
                <button className="dropdown-item" type="button" onClick={handleDeviceUpload}>
                  <i className="fi fi-rr-folder me-1" /> Choose From My Device
                </button>
              </li>
              <li>
                <button className="dropdown-item" type="button" onClick={() => window._draft_prompt_show_my_media?.()}>
                  <i className="fi fi-rr-picture me-1" /> Choose From My Media
                </button>
              </li>
            </ul>
          </div>

          {/* Reorder toggle */}
          {count > 1 && (
            <button
              type="button"
              className="btn btn-secondary rounded-circle mx-2"
              onClick={() => setShowReorder((v) => !v)}
            >
              <i className={`fi ${showReorder ? "fi-rr-check" : "fi-rr-arrows-repeat"} fs-6`} />
            </button>
          )}

          {/* Remove active */}
          {activeItem && (
            <button
              type="button"
              className="btn btn-secondary text-muted rounded-circle px-2 mx-1"
              onClick={() => itemRemove(activeItem.type, activeItem.id)}
            >
              <i className="fi fi-rr-trash fs-6" />
            </button>
          )}
        </div>
      )}


    </div>
  );
}